'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { BaseButtonProps } from 'src/app/_components/base/button';
import BaseButton from 'src/app/_components/base/button';
import { cn } from 'src/app/lib/utils/tailwindcss';

type ButtonProps = BaseButtonProps &
  VariantProps<typeof buttonVariants> & {
    icon?: ReactNode;
    iconPosition?: 'left' | 'right';
    isLoading?: boolean;
    children?: ReactNode;
  };

const buttonVariants = cva('inline-flex items-center justify-center gap-2 rounded-3xl font-bold transition-colors', {
  variants: {
    variant: {
      full: '',
      ghost: '',
      list: '',
      submit: '',
      icon: '',
    },
    size: {
      large: 'h-11 px-6 py-3 typo-heading-5',
      medium: 'h-10 py-2.5 px-6 typo-body-4',
      small: 'h-8 py-[7px] px-4 typo-body-7',
    },
    color: {
      primary: 'bg-k-50 text-white disabled:bg-k-20 hover:bg-k-60 active:bg-k-60',
      secondary: 'bg-g-90 text-white disabled:bg-g-30 hover:bg-g-80 active:bg-g-80',
      modalPrimary:
        'bg-modal-button-primary text-modal-button-primary-text hover:opacity-80 active:opacity-80 disabled:opacity-20',
      modalSecondary:
        'bg-modal-button-secondary text-modal-button-secondary-text hover:opacity-80 active:opacity-80 disabled:opacity-20',
    },
  },
  compoundVariants: [
    // ghost
    {
      variant: 'ghost',
      color: 'primary',
      className: cn(
        'bg-white border border-k-50 text-k-50',
        'hover:text-k-60 hover:bg-white active:text-k-60 active:bg-white',
        'disabled:text-k-20 disabled:border-k-20 disabled:bg-white',
      ),
    },
    {
      variant: 'ghost',
      color: 'secondary',
      className: cn(
        'bg-white border border-g-90 text-g-90',
        'hover:text-g-80 hover:bg-white active:text-g-80 active:bg-white',
        'disabled:text-g-30 disabled:border-g-30 disabled:bg-white',
      ),
    },
    // list
    {
      variant: 'list',
      className: cn(
        'rounded-sm border bg-white border-g-60 text-text-subtitle-2',
        'hover:bg-white hover:border-g-80 hover:text-g-80 active:bg-white active:border-g-80 active:text-g-80',
        'disabled:bg-white disabled:text-g-30 disabled:border-g-30',
      ),
    },
    // submit
    {
      variant: 'submit',
      className: cn(
        'rounded-lg bg-g-90 text-white',
        'hover:bg-g-80 active:bg-g-80',
        'disabled:text-white disabled:bg-g-30',
        'h-11',
        'py-[13px] px-2',
      ),
    },
    // icon
    {
      variant: 'icon',
      className: cn('py-3 px-5 font-normal'),
    },
  ],
  defaultVariants: {
    variant: 'full',
    size: 'medium',
    color: 'primary',
  },
});

const KottonseedButton = ({
  variant,
  size,
  color,
  icon,
  iconPosition = 'right',
  isLoading = false,
  className,
  children,
  disabled,
  asChild,
  ...props
}: ButtonProps) => {
  // 로딩 아이콘 생성
  const loadingIcon = isLoading ? (
    <div className="flex h-5 w-5 items-center justify-center">
      <Loader2 className="animate-spin" size={16} />
    </div>
  ) : null;

  // 일반 아이콘 요소
  const iconElement =
    !isLoading && icon ? <div className="flex h-5 w-5 items-center justify-center">{icon}</div> : null;

  if (asChild) {
    return (
      <BaseButton
        className={cn(buttonVariants({ variant, size, color }), 'cursor-pointer', className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {children}
      </BaseButton>
    );
  }
  return (
    <BaseButton
      className={cn(buttonVariants({ variant, size, color }), 'cursor-pointer', className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {iconPosition === 'left' && (loadingIcon || iconElement)}
      {children}
      {iconPosition === 'right' && (loadingIcon || iconElement)}
    </BaseButton>
  );
};

export default KottonseedButton;
